import React, { useState, useEffect, useRef } from 'react';
import { FaEnvelope, FaUser, FaDownload, FaBookOpen, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { subscribeNewsletter } from '../../../services/newsletter_subscription';
import { ClipLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import capa1 from "../../../images/news/newsletter/capa1.png";
import capa2 from "../../../images/news/newsletter/capa2.png";
import capa3 from "../../../images/news/newsletter/capa3.png";
import capa4 from "../../../images/news/newsletter/capa4.png";


gsap.registerPlugin(ScrollTrigger);

function Newsletter() {
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await subscribeNewsletter(formData);
      setFormData({ name: "", email: "" });
    } catch (error) {
      console.error("Subscription failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const newsletters = [
    {
      id: 4,
      title: "Newsletter Fall-Winter 2025/2026",
      image: capa4,
      pdfUrl: "/newsletter/pdf/Fall_Winter_25_26.pdf"
    },
    {
      id: 3,
      title: "Newsletter Spring-Summer 2025",
      image: capa3,
      pdfUrl: "/newsletter/pdf/Spring_Summer_25.pdf"
    },
    {
      id: 2,
      title: "Newsletter Fall-Winter 2024/2025",
      image: capa2,
      pdfUrl: "/newsletter/pdf/Fall_Winter_24_25.pdf"
    },
    {
      id: 1,
      title: "Newsletter Spring-Summer 2024",
      image: capa1,
      pdfUrl: "/newsletter/pdf/Spring_Summer_24.pdf"
    }
  ];

  const handleNewsletterClick = (newsletter) => {
    navigate(`/newsletter/${newsletter.id}`);
  };

  const nextSlide = () => setActiveSlide((prev) => (prev + 1) % newsletters.length);
  const prevSlide = () => setActiveSlide((prev) => (prev - 1 + newsletters.length) % newsletters.length);

  useEffect(() => {
    if (sectionRef.current) {
      gsap.fromTo('.newsletter-header',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out' }
      );
    }
  }, []);

  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(nextSlide, 4000);
      return () => clearInterval(interval);
    }
  }, [isPaused]);

  return (
    <section ref={sectionRef} className="relative w-full bg-slate-950 py-20 md:py-32 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-mainBlue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-mainBlue/5 rounded-full blur-3xl" />
      </div>

      <div 
        className="relative z-10 max-w-7xl mx-auto px-4 md:px-8"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="newsletter-header text-center mb-16 md:mb-24">
          <h2 className="font-custom text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4">Newsletter</h2>
          <div className="h-1 bg-mainBlue mx-auto mb-6 w-24" />
          <p className="text-gray-400 text-base  max-w-2xl mx-auto font-custom">
            Receive updates from our team every semester. Subscribe now to stay informed about our latest projects and achievements.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center justify-center">

          <div className="order-2 md:order-1 flex flex-col items-center bg-slate-900/50 backdrop-blur-sm p-6 md:p-8 rounded-2xl w-full md:w-96 border border-white/10 min-h-[400px]">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-6">Subscribe to Our Newsletter</h3>
            <form className="flex flex-col items-center w-full" onSubmit={handleSubmit}>
              <div className="relative w-full mt-5">
                <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full text-white bg-slate-800/50 border border-white/10 rounded-xl p-4 pl-12 outline-none focus:border-mainBlue focus:ring-2 focus:ring-mainBlue/20 transition-all placeholder-gray-500"
                  type="text"
                  placeholder="Your Name"
                  required
                />
              </div>
              <div className="relative w-full mt-4">
                <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full text-white bg-slate-800/50 border border-white/10 rounded-xl p-4 pl-12 outline-none focus:border-mainBlue focus:ring-2 focus:ring-mainBlue/20 transition-all placeholder-gray-500"
                  type="email"
                  placeholder="Your Email"
                  required
                />
              </div>
              <button 
                className="text-lg font-bold bg-mainBlue text-white hover:bg-blue-600 rounded-xl mt-6 px-8 py-3 w-full shadow-lg hover:scale-105 transition-all duration-300" 
                type="submit" 
                disabled={loading}
              >
                {loading ? (
                  <ClipLoader size={24} color={"#ffffff"} loading={loading} />
                ) : (
                  "SUBSCRIBE"
                )}
              </button>
            </form>
          </div>

          <div className="order-1 md:order-2 flex flex-col items-center w-full md:w-80 lg:w-96">
            <div className="relative w-full px-8">
              <div className="relative flex items-center justify-center">
                <button onClick={prevSlide} className="absolute left-0 z-10 text-white/70 hover:text-white hover:scale-110 transition-all p-2">
                  <FaChevronLeft size={24} />
                </button>
                <div key={activeSlide} className="bg-slate-900/50 backdrop-blur-sm rounded-2xl p-6 flex flex-col items-center border border-white/10">
                  <img 
                    src={newsletters[activeSlide].image} 
                    alt={newsletters[activeSlide].title} 
                    className="w-48 h-64 object-contain rounded-lg shadow-2xl mb-4 hover:scale-105 transition-transform cursor-pointer"
                    onClick={() => handleNewsletterClick(newsletters[activeSlide])}
                  />
                  <h3 className="text-white text-lg font-semibold mb-4 text-center">{newsletters[activeSlide].title}</h3>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => handleNewsletterClick(newsletters[activeSlide])}
                      className="flex items-center gap-2 bg-mainBlue hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <FaBookOpen />
                      <span>Read</span>
                    </button>
                    <a 
                      href={newsletters[activeSlide].pdfUrl} 
                      download 
                      className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <FaDownload />
                      <span>PDF</span>
                    </a>
                  </div>
                </div>
                <button onClick={nextSlide} className="absolute right-0 z-10 text-white/70 hover:text-white hover:scale-110 transition-all p-2">
                  <FaChevronRight size={24} />
                </button>
              </div>
              <div className="flex justify-center gap-3 mt-4">
                {newsletters.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      activeSlide === index ? 'bg-mainBlue w-6' : 'bg-white/30 hover:bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Newsletter;
